import type { CreateAsset, GenerationSubtype } from '../models/project.model';

/** Card title only. Prompts are deliberately never used as card copy. */
export function assetDisplayTitle(
  asset: Partial<Pick<CreateAsset, 'title'>> | null | undefined,
  localizedFallback = '',
): string {
  const title = typeof asset?.title === 'string' ? asset.title.trim() : '';
  return title || localizedFallback;
}

/**
 * Localized kind fallback while an asynchronous generated title is pending.
 *
 * A missing asset yields the kind-neutral key rather than falling through to
 * audio: callers routinely ask before the asset has loaded, and labelling every
 * loading image and video "Untitled audio" was simply wrong.
 */
export function assetUntitledTranslationKey(
  asset: Partial<Pick<CreateAsset, 'type' | 'generationSubtype'>> | null | undefined,
): string {
  if (!asset) return 'asset.untitled';
  const subtype = asset.generationSubtype as GenerationSubtype | undefined;
  if (subtype === 'music') return 'asset.untitled-music';
  if (subtype === 'speech') return 'asset.untitled-narration';
  if (subtype === 'video' || asset.type === 'video') return 'asset.untitled-video';
  if (subtype === 'image' || asset.type === 'image') return 'asset.untitled-image';
  if (asset.type === 'audio') return 'asset.untitled-audio';
  return 'asset.untitled';
}

/** A title can land on any version while mixed backend versions roll out. */
export function generatedLineageTitle(versions: readonly CreateAsset[]): string | undefined {
  return [...versions]
    .reverse()
    .map((version) => (typeof version.title === 'string' ? version.title.trim() : ''))
    .find((title): title is string => !!title);
}
